/**
 * Workflow Manager — Master Permission Configuration
 *
 * SSOT for the system permission matrix.
 * Separates what a user can DO (RBAC) from what they can SEE (scope — see scopeService.ts).
 *
 * System roles (4 official + 1 special):
 *   owner          → Full control. Equivalent to consultor_master (CanonicalRole).
 *   admin          → Administrative access. Full module access, no billing destruction.
 *   operator       → Operational user. Create/edit but no delete or administration.
 *   viewer         → Read-only across modules.
 *   external_client→ Restricted portal access (analytics + projects read-only).
 *
 * Modules:
 *   workflow, vsm_canvas, blueprint, problems, actions,
 *   projects, analytics, capacity, administration
 */

// ─── Types ─────────────────────────────────────────────────────────────────────

export type SystemRole = 'owner' | 'admin' | 'operator' | 'viewer' | 'external_client';

export type ModuleAction = 'view' | 'create' | 'edit' | 'delete';

export type AppModule =
  | 'workflow'
  | 'vsm_canvas'
  | 'blueprint'
  | 'problems'
  | 'actions'
  | 'projects'
  | 'analytics'
  | 'capacity'
  | 'administration';

// ─── Role Display Config ────────────────────────────────────────────────────────

export interface SystemRoleMeta {
  key: SystemRole;
  label: string;
  description: string;
  color: string;
}

export const SYSTEM_ROLES: SystemRoleMeta[] = [
  {
    key: 'owner',
    label: 'Owner',
    description: 'Controle total sobre a organização e plataforma.',
    color: 'border-violet-400',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Acesso administrativo completo. Gerencie usuários e configurações.',
    color: 'border-sky-400',
  },
  {
    key: 'operator',
    label: 'Operador',
    description: 'Usuário operacional — cria, edita e executa fluxos e ações.',
    color: 'border-emerald-400',
  },
  {
    key: 'viewer',
    label: 'Visualizador',
    description: 'Leitura somente. Sem capacidade de criar, editar ou excluir.',
    color: 'border-amber-400',
  },
  {
    key: 'external_client',
    label: 'Cliente Externo',
    description: 'Acesso restrito ao portal do cliente — analytics e projetos.',
    color: 'border-slate-400',
  },
];

// ─── Module Display Config ──────────────────────────────────────────────────────

export interface AppModuleMeta {
  key: AppModule;
  label: string;
}

export const APP_MODULES: AppModuleMeta[] = [
  { key: 'workflow',       label: 'Workflows' },
  { key: 'vsm_canvas',    label: 'Mapa de Fluxos (VSM)' },
  { key: 'blueprint',     label: 'Blueprint' },
  { key: 'problems',      label: 'Problemas' },
  { key: 'actions',       label: 'Central de Ações' },
  { key: 'projects',      label: 'Projetos' },
  { key: 'analytics',     label: 'Analytics' },
  { key: 'capacity',      label: 'Capacidade' },
  { key: 'administration',label: 'Administração' },
];

// ─── Permission Matrix ──────────────────────────────────────────────────────────

type ModuleMatrix = Record<AppModule, ModuleAction[]>;
type PermissionMatrix = Record<SystemRole, ModuleMatrix>;

const ALL: ModuleAction[] = ['view', 'create', 'edit', 'delete'];
const WRITE: ModuleAction[] = ['view', 'create', 'edit'];
const READ: ModuleAction[] = ['view'];
const NONE: ModuleAction[] = [];

export const PERMISSION_MATRIX: PermissionMatrix = {
  owner: {
    workflow:       ALL,
    vsm_canvas:     ALL,
    blueprint:      ALL,
    problems:       ALL,
    actions:        ALL,
    projects:       ALL,
    analytics:      ALL,
    capacity:       ALL,
    administration: ALL,
  },
  admin: {
    workflow:       ALL,
    vsm_canvas:     WRITE,
    blueprint:      ALL,
    problems:       ALL,
    actions:        ALL,
    projects:       ALL,
    analytics:      WRITE,
    capacity:       WRITE,
    administration: ALL,
  },
  operator: {
    workflow:       WRITE,
    vsm_canvas:     ['view', 'edit'],
    blueprint:      READ,
    problems:       WRITE,
    actions:        WRITE,
    projects:       ['view', 'create'],
    analytics:      READ,
    capacity:       READ,
    administration: NONE,
  },
  viewer: {
    workflow:       READ,
    vsm_canvas:     READ,
    blueprint:      READ,
    problems:       READ,
    actions:        READ,
    projects:       READ,
    analytics:      READ,
    capacity:       READ,
    administration: NONE,
  },
  external_client: {
    workflow:       NONE,
    vsm_canvas:     NONE,
    blueprint:      NONE,
    problems:       NONE,
    actions:        NONE,
    projects:       READ,
    analytics:      READ,
    capacity:       NONE,
    administration: NONE,
  },
};

// ─── CanonicalRole → SystemRole mapping ─────────────────────────────────────────
// Bridges the existing src/permissions.ts CanonicalRole with SystemRole.

import type { CanonicalRole } from '../permissions';

const CANONICAL_TO_SYSTEM: Record<CanonicalRole, SystemRole> = {
  consultor_master: 'owner',
  admin:            'admin',
  gestor:           'operator',
  usuario:          'operator',
  viewer:           'viewer',
};

export function canonicalToSystemRole(canonical: CanonicalRole | null): SystemRole {
  if (!canonical) return 'viewer';
  return CANONICAL_TO_SYSTEM[canonical] ?? 'viewer';
}

// ─── Pure permission check ───────────────────────────────────────────────────────

/**
 * Checks if a system role can perform an action on a module.
 * Stateless — safe to use outside React (services, tests).
 */
export function canDo(
  role: SystemRole,
  action: ModuleAction,
  module: AppModule,
): boolean {
  return PERMISSION_MATRIX[role][module].includes(action);
}
